import authentication from "@irati/auth"
import {Deal, PaymentCardInput, PaymentOrder, PaymentWalletInput, RewardOrder, PaymentZipInput, Wallet, WellnessMedia} from "@irati/types"
import RewardService from "@irati/reward/apollo/Reward"
import StatementItemTypes from "@irati/wallet/types/enums/StatementItemTypes"
import {getNormalisedValue} from "@irati/utils/currency/formatCurrency"

import {PaymentProvider, WalletType} from "../enums/PaymentV2"
import {PaymentCardRequest, PaymentWalletRequest, PaymentZipRequest} from "../services/Payments"
import channel from "./channel"

export default function(provider: PaymentProvider, paymentOrder: PaymentOrder, details: PaymentCardRequest): PaymentCardInput {
  switch (provider) {
    case PaymentProvider.Adyen:
      return makeAdyenDropInPayload(paymentOrder, details)
    default:
      throw new Error("Unknown payment provider")
  }
}

function mapLocaleToAdyenLocale(locale: string) {
  switch (locale) {
    case "fr-FR":
      return "fr"
    default:
      return "en_GB"
  }
}

function makeAdyenDropInPayload(paymentOrder: PaymentOrder, details: PaymentCardRequest): PaymentCardInput {
  return {
    provider: PaymentProvider.Adyen,
    currency: paymentOrder.currency,
    amount: details.amount,
    enableSaveCardOption: details.enableSaveCardOption,
    details: {
      shopperEmail: authentication.user().email,
      shopperLocale: mapLocaleToAdyenLocale(authentication.userLocale()),
      channel
    }
  }
}

export function makeWellnessPaymentPayload(
  paymentOrder: PaymentOrder,
  wallet: Wallet,
  details: PaymentWalletRequest,
  media: WellnessMedia
): PaymentWalletInput {
  return {
    provider: PaymentProvider.Wallet,
    currency: paymentOrder.currency,
    amount: details.amount,
    details: {
      sourceOwnerId: wallet.ownerUuid,
      sourceWalletId: wallet.uuid,
      reference: {
        orderTotal: {
          currency: paymentOrder.currency,
          value: getNormalisedValue(true, paymentOrder.currency, details.amount)
        },
        type: StatementItemTypes.SPEND,
        dealHeader: media.title,
        dealThumbnailImage: media.thumbnail,
        isWellnessRental: true
      }
    }
  }
}

export function makePaymentWalletPayload(
  paymentOrder: PaymentOrder,
  wallet: Wallet,
  details: PaymentWalletRequest,
  deal: Deal,
  redemptionId: string
): PaymentWalletInput {
  return {
    provider: PaymentProvider.Wallet,
    currency: paymentOrder.currency,
    amount: details.amount,
    details: {
      sourceOwnerId: wallet.ownerUuid,
      sourceWalletId: wallet.uuid,
      reference: {
        dealHeader: deal.header,
        dealThumbnailImage: deal.thumbnailImage,
        orderTotal: {
          currency: paymentOrder.currency,
          value: getNormalisedValue(true, paymentOrder.currency, details.amount)
        },
        redemptionUuid: redemptionId,
        type: StatementItemTypes.SPEND
      }
    }
  }
}

export function makePaymentRewardWalletPayload(
  paymentOrder: PaymentOrder,
  details: PaymentWalletRequest,
  rewardOrder: RewardOrder
): Promise<PaymentWalletInput> {
  return RewardService.getWallet().then((rewardWallet) => {
    return {
      provider: PaymentProvider.Wallet,
      currency: paymentOrder.currency,
      amount: details.amount,
      details: {
        sourceOwnerId: rewardWallet.ownerUuid,
        sourceWalletId: rewardWallet.walletUuid,
        walletType: WalletType.Rewards,
        reference: {
          service: "rewards-wallet",
          version: "FE",
          type: "use-credit-to-buy-reward",
          order_id: rewardOrder.uuid,
          rewards_wallet_id: rewardWallet.uuid
        }
      }
    }
  })
}

export function makeZipPayload(paymentOrder: PaymentOrder, details: PaymentZipRequest): PaymentZipInput {
  return {
    provider: PaymentProvider.Zip,
    currency: paymentOrder.currency,
    amount: details.amount,
    details: {
      resURL: details.resUrl
    }
  }
}
